import _defineProperty from "@babel/runtime/helpers/defineProperty";
// import {RemoteAbInitioErrorException} from "abweb/dto/exception/RemoteAbInitioErrorException";

/**
 * Support NestedError that reports the underlying 'causedBy' error (which itself can be a NestedError, thereby
 * supported nested error 'chains').
 * <p>
 * Note this NestedError implementation tries to stay pretty close to how java natively handles nested errors.
 * The Java native exception behavior is documented in javacommons/utils:<br>
 *     ExceptionUtilsTest::testIllustrateWrappedExceptionBehavior
 * Minor difference: for convenience the nestedError.toString() does recursive chaining, while java does not.
 * Thus we have three different use cases supported:
 * <ol>
 *   <li> Simple Message: use nestedError.message.   Handy when wrapping exceptions with a sanitized version where
 *      don't want the gory underlying messages to bleed through.
 *      <ul>
 *        <li>Example: "outer message"
 *      </ul>
 *   <li> Chained message: use nestedError.toString().  Show message causal chain,
 *      but without the gory stack trace information.
 *      <ul>
 *        <li>Example: "Error: outer message\n\tCaused by Error: underlying message"
 *      </ul>
 *   <li> Complex Debugging: use nestedError.stack.  Note that at each level of the caused by stack,
 *      the message is also displayed in addition to the stack (file:lineNum)
 *      <ul><li><code>
 *  Example: Error: My nested error1
 *     at UserContext.eval (http://bmiller-10.abinitio.com:9878/base/webapp/abweb/test/specs/dto/exception/NestedErrorUSpec.js:68:39)
 *     ...  // brad chopped
 *     at MessagePort.channel.port1.onmessage (http://bmiller-10.abinitio.com:9878/absoluteC:/sand/ace-bre-dev/webcommons/abinitio/build/node-install/karma-jasmine/node_modules/jasmine-core/lib/jasmine-core/jasmine.js:2712:11)
 *  Caused by: Error: string err msg
 *     at UserContext.eval (http://bmiller-10.abinitio.com:9878/base/webapp/abweb/test/specs/dto/exception/NestedErrorUSpec.js:66:37)
 *     ...  // brad chopped
 *     at MessagePort.channel.port1.onmessage (http://bmiller-10.abinitio.com:9878/absoluteC:/sand/ace-bre-dev/webcommons/abinitio/build/node-install/karma-jasmine/node_modules/jasmine-core/lib/jasmine-core/jasmine.js:2712:11)
 *     </code></li></ul>
 * </ol>
 */
export class NestedError extends Error {
  constructor(errorMsg, causedBy) {
    // Typescript compiler will accept super(errorMsg) just fine and emit the corresponding javascript
    // However, the browser js runtime can still refuse to allow a builtin to be extended, and thus the msgArg is 'lost'
    // super(errorMsg); // does NOT work in Chrome (and perhaps other browsers as well)

    // Workaround: See https://github.com/Microsoft/TypeScript/issues/1168#issuecomment-112955503
    //   specifically comment by 'unional' on Jun 2, 2015
    super();
    _defineProperty(this, "causedBy", void 0);
    _defineProperty(this, "_inNestedErrorConstructor", true);
    this.causedBy = typeof causedBy === "string" ? new Error(causedBy) : causedBy; // can be Error | null

    // nestedError.toString() will 'chain' nested error messages, nestedError.message will not
    // This allows NestedErrors to 'wrap' underlying exceptions and
    // show cleaner messages without underlying cause message bleeding through (if you want underlying causes
    // invoke nestedError.toString()...)
    this.message = errorMsg;
    let calcStack = this.adjustStack(this);
    if (this.causedBy) {
      calcStack += NestedError.causedByPrefix + (/*"XXCBXX" +*/
      // Do NOT augment the stack if a string was passed in as will provide no additional info
      //   over this.stack (will be identical to NestedError stack)
      typeof causedBy === "string" ? this.causedBy.toString() // essentially the header of the below stack without raw stack
      : this.adjustStack(this.causedBy));
    }
    this.stack = calcStack;
    this._inNestedErrorConstructor = false;
  }

  // Chrome error.stack includes 'Error: $msg' at each level of the nested exception chain in
  // addition to the raw stack trace (list of file:lineNum).
  // Firefox only includes the raw stack stack.  This adds message to the raw firefox stack
  // so that the NestedError.stack is somewhat normalized.
  // One remaining issue: format of the firefox is ugly, and different that Chrome raw stack.
  // Could spend time trying to normalize the output.
  adjustStack(err) {
    const rawStack = err.stack;
    if (rawStack == null) {
      return "Error: " + err.message;
    }
    return rawStack.startsWith("Error: ") ? rawStack // Chromium, Edge
    // Firefox does not include message in stack (and has really ugly stack)
    : /*"XXXASXXX" +*/"Error: " + err.message + "\n\t at " + rawStack;
  }

  /**
   * <p>This implementation will recursively 'chain' nested error messages.
   * Use nestedError.message only shows nested error message (no chaining)
   *
   * <p>Note: if you override this toString method, note that the override will be called by
   * pre-chromium Edge from the NestedError constructor as part of the internal stack calculation.
   * Developers can short-circuit their override implementation to do something stack appropriate
   * (typically 'return super.toString()) when this.inNestedErrorConstructorGuard is true.
   */
  toString() {
    // In pre-chromium edge, calling this.stack in constructor internally calls this toString()
    // method.  Do NOT want chained messages as part of the stack, so only return
    // the unchained toString when in constructor
    return this.inNestedErrorConstructor || !this.causedBy ? super.toString() : super.toString() + NestedError.causedByPrefix /*+ "XXTSXX"*/ + this.causedBy.toString();
  }

  // no setter
  /**
   * Meant to only be used with child class 'toString()' override methods.
   * @see toString
   */
  get inNestedErrorConstructor() {
    return this._inNestedErrorConstructor;
  }
}
_defineProperty(NestedError, "causedByPrefix", "\n\tCaused by: ");
//# sourceMappingURL=NestedError.js.map
